import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import type { DeclarationHandlers } from '../types'

type CommandBarDeclarationContextValue = {
  handlers: DeclarationHandlers | null
  registerHandlers: (handlers: DeclarationHandlers) => void
  unregisterHandlers: () => void
}

const CommandBarDeclarationContext =
  createContext<CommandBarDeclarationContextValue | null>(null)

export function CommandBarDeclarationProvider({
  children,
}: {
  children: ReactNode
}) {
  const [handlers, setHandlers] = useState<DeclarationHandlers | null>(null)

  const registerHandlers = useCallback((next: DeclarationHandlers) => {
    setHandlers(next)
  }, [])

  const unregisterHandlers = useCallback(() => {
    setHandlers(null)
  }, [])

  const value = useMemo(
    () => ({
      handlers,
      registerHandlers,
      unregisterHandlers,
    }),
    [handlers, registerHandlers, unregisterHandlers]
  )

  return (
    <CommandBarDeclarationContext.Provider value={value}>
      {children}
    </CommandBarDeclarationContext.Provider>
  )
}

export function useCommandBarDeclaration() {
  const context = useContext(CommandBarDeclarationContext)
  if (!context) {
    throw new Error(
      'useCommandBarDeclaration must be used within CommandBarDeclarationProvider'
    )
  }
  return context
}
